'use client';

import { useState } from 'react';
import FormInput from '@/components/common/form/FormInput';
import FormTextArea from '@/components/common/form/FormTextArea';
import { apiConfig } from '@/config/api';

interface GeoAuthoritySectionProps {
    isEditing: boolean;
    setIsEditing: (editing: boolean) => void;
    isSaving: boolean;
    handleSave: () => void;
    handleCancel: () => void;
    // GEO Authority Fields
    jobTitle: string;
    setJobTitle: (val: string) => void;
    bio: string;
    setBio: (val: string) => void;
    linkedinData: string;
    setLinkedinData: (val: string) => void;
    twitterData: string;
    setTwitterData: (val: string) => void;
}

export default function GeoAuthoritySection({
    isEditing,
    setIsEditing,
    isSaving,
    handleSave,
    handleCancel,
    jobTitle,
    setJobTitle,
    bio,
    setBio,
    linkedinData,
    setLinkedinData,
    twitterData,
    setTwitterData
}: GeoAuthoritySectionProps) {
    return (
        <div className="space-y-4">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <span>🚀 Author Authority (GEO)</span>
                    <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700 font-normal">Boosts AI Trust</span>
                </h4>
                {!isEditing && (
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                    >
                        Edit
                    </button>
                )}
            </div>

            {isEditing ? (
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                        <FormInput
                            type="text"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            placeholder="e.g. Senior Marketing Specialist"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Professional Bio</label>
                        <FormTextArea
                            rows={3}
                            value={bio}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder="Briefly describe your expertise (e.g. '15+ years experience in digital strategy...')"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                            <FormInput
                                type="url"
                                value={linkedinData}
                                onChange={(e) => setLinkedinData(e.target.value)}
                                placeholder="https://linkedin.com/in/..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Twitter/X URL</label>
                            <FormInput
                                type="url"
                                value={twitterData}
                                onChange={(e) => setTwitterData(e.target.value)}
                                placeholder="https://twitter.com/..."
                            />
                        </div>
                    </div>

                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={isSaving}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
                        <div className="md:col-span-2">
                            <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Professional Title</dt>
                            <dd className="text-sm font-medium text-gray-900">{jobTitle || <span className="text-gray-400 italic">Not set (Recommended for Authority)</span>}</dd>
                        </div>

                        <div className="md:col-span-2">
                            <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Professional Bio</dt>
                            <dd className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                {bio || <span className="text-gray-400 italic">No bio provided. Adding a bio helps AI understand your expertise.</span>}
                            </dd>
                        </div>

                        <div className="md:col-span-2">
                            <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Verified Social Signals</dt>
                            <dd className="flex flex-wrap gap-3">
                                {linkedinData ? (
                                    <a href={linkedinData} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                                        <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                                        LinkedIn
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200 border-dashed">
                                        + Add LinkedIn
                                    </span>
                                )}

                                {twitterData ? (
                                    <a href={twitterData} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200 transition-colors">
                                        <svg className="w-3.5 h-3.5 mr-1.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" /></svg>
                                        X (Twitter)
                                    </a>
                                ) : (
                                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200 border-dashed">
                                        + Add X (Twitter)
                                    </span>
                                )}
                            </dd>
                        </div>
                    </dl>
                </div>
            )}
        </div>
    );
}
